import { useAuth } from 'react-oidc-context'
import { useQuery } from '@tanstack/react-query'
import type { Permission } from '@/auth/permissions'
import { hasPermission, getPermissions, getRoles, getTenantSlugs } from '@/auth/permissions'
import { useApiClient } from '@/api/client'
import type { SupportGrant, SupportGrantSection } from '@/types'

/**
 * Returns true if the authenticated user has the given permission.
 * Always returns false when the user is not authenticated.
 */
export function usePermission(perm: Permission): boolean {
  const { user } = useAuth()
  return hasPermission(user, perm)
}

/**
 * Returns the full set of permissions for the current user.
 */
export function usePermissions(): Set<Permission> {
  const { user } = useAuth()
  if (!user) return new Set()
  return getPermissions(user)
}

/**
 * Returns the platform roles assigned to the current user.
 */
export function useRoles() {
  const { user } = useAuth()
  if (!user) return []
  return getRoles(user)
}

/**
 * Returns the list of tenant slugs the current user belongs to.
 * platform-admin users see all tenants, so this list may be empty for them.
 */
export function useTenantSlugs(): string[] {
  const { user } = useAuth()
  if (!user) return []
  return getTenantSlugs(user)
}

/**
 * For a platform-admin-only user (platform-admin WITHOUT tenant-admin):
 * returns the set of support grant sections currently active for the given tenant.
 *
 * Returns null for:
 *   - users who are not platform-admin
 *   - users who have both platform-admin AND tenant-admin (they bypass grant checks)
 *   - when no tenantId is selected
 *
 * Use this to decide whether to show locked/disabled nav items for a platform-admin.
 */
export function usePlatformAdminGrantSections(tenantId: string | null): Set<SupportGrantSection> | null {
  const { user } = useAuth()
  const api = useApiClient()

  const roles = user ? getRoles(user) : []
  const isPlatformAdminOnly = roles.includes('platform-admin') && !roles.includes('tenant-admin')

  const { data: grants } = useQuery({
    queryKey: ['all-support-grants'],
    queryFn: () => api.get<SupportGrant[]>('/api/v1/admin/support-grants'),
    enabled: isPlatformAdminOnly && !!tenantId,
    staleTime: 15_000,
  })

  if (!isPlatformAdminOnly || !tenantId) return null

  const now = new Date()
  const sections = new Set<SupportGrantSection>()
  for (const g of grants ?? []) {
    if (g.tenant_id !== tenantId) continue
    if (g.revoked_at) continue
    if (g.expires_at && new Date(g.expires_at) < now) continue
    for (const s of g.sections) sections.add(s)
  }
  return sections
}
